import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import {
  Activity, Search, RefreshCw, MapPin, Users, Filter, X, LogOut,
  ChevronRight, Sparkles, BadgeCheck, Repeat, RotateCcw, Info,
  TrendingUp, TrendingDown, Minus, Gauge,
} from "lucide-react";

const POLL_MS = 30000;

const METHOD_META = {
  kiosk_normal: { label: "Standard", icon: BadgeCheck, color: "text-navy-500" },
  kiosk_pre_reg: { label: "Pre-reg", icon: Sparkles, color: "text-orange-600" },
  kiosk_returning: { label: "Returning", icon: Repeat, color: "text-success" },
};

export default function LiveDashboard() {
  // Phase B2 — every "signed in at" timestamp goes through useLocale so an
  // org-level locale flip is reflected on the live floor immediately.
  const { formatDateTime, formatTime, formatDate } = useLocale();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: "", visitor_type: "", host_id: "", q: "" });
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [drawerVisitId, setDrawerVisitId] = useState(null);
  const [forceTarget, setForceTarget] = useState(null);
  const [tick, setTick] = useState(0);
  // Phase C1.6 — Returning-Visitor ROI widget state. Loaded alongside the
  // main "who's on site" query on the same poll cycle to keep the figure
  // fresh without spinning up a separate poller.
  const [rvStats, setRvStats] = useState(null);
  // Phase C2 follow-on — Solo-plan monthly visit-quota chip. Fetched once
  // on mount; non-Solo orgs get `plan_id` other than "solo" and the chip
  // stays hidden. Failures fall through silently (host/contractor roles
  // legitimately 403 on /subscriptions/usage).
  const [quota, setQuota] = useState(null);
  const intervalRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.visitor_type) params.visitor_type = filters.visitor_type;
      if (filters.host_id) params.host_id = filters.host_id;
      if (filters.q) params.q = filters.q;
      const [{ data }, statsRes] = await Promise.all([
        api.get("/live-dashboard/visitors-on-site", { params }),
        api.get("/live-dashboard/returning-visitor-stats", {
          params: {
            ...(filters.site_id ? { site_id: filters.site_id } : {}),
            // Phase C1.7 — pull the trailing 7 days alongside today's split
            // so the FastLaneCard sparkline + delta chip can render on the
            // same paint as the headline number.
            include_history: 7,
          },
        }).catch(() => null),
      ]);
      setItems(data.items || []);
      setCount(data.count || 0);
      setLastRefreshed(data.last_refreshed_at);
      setElapsedMs(data.elapsed_ms || 0);
      if (statsRes?.data) setRvStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial + polling
  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [filters.site_id, filters.visitor_type, filters.host_id, filters.q]);

  // Tick the "12s ago" label every second
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Filter dropdown sources
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    api.get("/visitor-types").then((r) => setTypes((r.data || []).filter((t) => t.active !== false))).catch(() => {});
    api.get("/users").then((r) => setHosts((r.data || []).filter((u) => u.role === "host"))).catch(() => {});
    // Phase C2 follow-on — solo-quota chip is hidden for non-solo plans so
    // a single fetch on mount is enough. Re-rendered after each sign-in
    // would be ideal but unnecessary for a 100/month gauge that only
    // updates roughly every few minutes in practice.
    api.get("/subscriptions/usage").then((r) => setQuota(r.data || null)).catch(() => setQuota(null));
  }, []);

  const sinceLabel = useMemo(() => {
    if (!lastRefreshed) return "—";
    const ms = Date.now() - new Date(lastRefreshed).getTime();
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ${s % 60}s ago`;
    // eslint-disable-next-line
  }, [lastRefreshed, tick]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="eb-overline mb-2">Module 9 · Live</div>
          <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="live-heading">
            <Activity className="w-7 h-7 text-orange-500" />
            Who's on site now
            <span className="text-sm bg-navy-900 text-white px-2 py-0.5 rounded-full font-bold" data-testid="live-count">
              {count}
            </span>
          </h1>
          <p className="text-sm text-navy-500 mt-1">
            Auto-refreshing every {POLL_MS / 1000}s · last update <span data-testid="live-since">{sinceLabel}</span>
            {elapsedMs ? <span className="text-navy-400"> · {elapsedMs}ms</span> : null}
          </p>
        </div>
        <button onClick={load} className="btn-outline text-xs" data-testid="live-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Phase C2 follow-on — Solo-plan monthly visit-quota chip.
          Hidden entirely for non-solo plans and when the API hasn't
          responded yet. Thresholds: green ≤70 %, amber 70-89 %, red 90 %+. */}
      {quota && quota.plan_id === "solo" && quota.visits_limit > 0 && (() => {
        const used = quota.visits_this_month || 0;
        const cap = quota.visits_limit;
        const pct = used / cap;
        const cls =
          pct >= 0.9 ? "border-danger/40 bg-danger/5 text-danger-700"
          : pct >= 0.7 ? "border-warning/40 bg-warning/5 text-warning-700"
          : "border-success/40 bg-success/5 text-success-700";
        return (
          <div
            className={`mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-full border ${cls}`}
            data-testid="solo-quota-chip"
          >
            <Gauge className="w-4 h-4" />
            <span className="text-sm font-semibold">
              {used}/{cap} visits this month
            </span>
            <span className="text-xs opacity-80">· Solo plan</span>
          </div>
        );
      })()}

      {/* Phase C1.6 — Returning Visitor ROI widget. Hidden if the API
          didn't return anything (e.g. host/contractor role 403'd) so we
          don't render a half-broken card. */}
      {rvStats && (
        <FastLaneCard
          stats={rvStats}
          formatDate={formatDate}
          data-testid="fast-lane-card"
        />
      )}

      {/* Search + filter chips */}
      <div className="eb-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
          <input
            value={filters.q}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Search name / company / vehicle reg…"
            className="eb-input pl-9"
            data-testid="live-search-input"
          />
        </div>
        <FilterPill icon={MapPin} label="Site" value={filters.site_id} onChange={(v) => setFilter("site_id", v)}
          options={[{ id: "", label: "All sites" }, ...sites.map((s) => ({ id: s.id, label: s.name }))]}
          testid="live-filter-site"
        />
        <FilterPill icon={Filter} label="Type" value={filters.visitor_type} onChange={(v) => setFilter("visitor_type", v)}
          options={[{ id: "", label: "All types" }, ...types.map((t) => ({ id: t.name, label: t.name }))]}
          testid="live-filter-type"
        />
        <FilterPill icon={Users} label="Host" value={filters.host_id} onChange={(v) => setFilter("host_id", v)}
          options={[{ id: "", label: "All hosts" }, ...hosts.map((h) => ({ id: h.id, label: h.name || h.email }))]}
          testid="live-filter-host"
        />
        {(filters.site_id || filters.visitor_type || filters.host_id || filters.q) && (
          <button onClick={() => setFilters({ site_id: "", visitor_type: "", host_id: "", q: "" })}
            className="text-[11px] text-navy-500 hover:text-orange-600" data-testid="live-clear-filters">
            <X className="w-3.5 h-3.5 inline" /> Clear
          </button>
        )}
      </div>

      <div className="eb-card p-0 overflow-hidden" data-testid="live-table">
        {loading && items.length === 0 ? (
          <p className="py-10 text-center text-navy-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <Activity className="w-10 h-10 text-navy-300 mx-auto mb-3" />
            <p className="text-sm text-navy-500">No-one is signed in right now.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-navy-50/60 text-navy-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3"></th>
                <th className="text-left px-4 py-3">Visitor</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Site</th>
                <th className="text-left px-4 py-3">Host</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Signed in</th>
                <th className="text-left px-4 py-3">Duration</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const meta = METHOD_META[it.sign_in_method] || METHOD_META.kiosk_normal;
                const Mi = meta.icon;
                return (
                  <tr key={it.visit_id}
                    className="border-t border-navy-100 hover:bg-navy-50/40 cursor-pointer"
                    onClick={() => setDrawerVisitId(it.visit_id)}
                    data-testid={`live-row-${it.visit_id}`}>
                    <td className="px-4 py-3">
                      {it.photo_thumb_url ? (
                        <img src={it.photo_thumb_url} alt="" className="w-10 h-10 rounded-md object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-navy-100 text-navy-500 font-bold flex items-center justify-center">
                          {(it.visitor_name || "?").slice(0, 1)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <div>{it.visitor_name}</div>
                      <div className="text-[10px] text-navy-400 inline-flex items-center gap-1">
                        <Mi className={`w-3 h-3 ${meta.color}`} /> {meta.label}
                      </div>
                    </td>
                    <td className="px-4 py-3">{it.company || "—"}</td>
                    <td className="px-4 py-3">{it.site_name || it.site_id?.slice(0, 8)}</td>
                    <td className="px-4 py-3">{it.host_name || "—"}</td>
                    <td className="px-4 py-3">{it.visitor_type}</td>
                    <td className="px-4 py-3 text-navy-500 text-xs">
                      {it.signed_in_at ? formatTime(it.signed_in_at) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-mono px-2 py-1 rounded ${
                        it.duration_minutes > 240 ? "bg-warning/15 text-warning" :
                        it.duration_minutes > 60 ? "bg-navy-50 text-navy-600" :
                        "bg-success/15 text-success"
                      }`}>{it.duration_minutes}m</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setForceTarget(it); }}
                        className="text-xs text-danger hover:underline inline-flex items-center gap-1"
                        data-testid={`live-force-${it.visit_id}`}
                      >
                        <LogOut className="w-3 h-3" /> Force
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {drawerVisitId && (
        <VisitDetailDrawer
          visitId={drawerVisitId}
          onClose={() => setDrawerVisitId(null)}
          onForce={(it) => setForceTarget(it)}
        />
      )}
      {forceTarget && (
        <ForceModal
          visit={forceTarget}
          onClose={() => setForceTarget(null)}
          onDone={() => { setForceTarget(null); setDrawerVisitId(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function FilterPill({ icon: Icon, label, value, onChange, options, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5 bg-white border border-navy-100 rounded-md px-2 py-1.5 text-xs">
      <Icon className="w-3.5 h-3.5 text-navy-400" />
      <span className="text-navy-500">{label}:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none text-navy-700"
        data-testid={testid}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

function VisitDetailDrawer({ visitId, onClose, onForce }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/live-dashboard/visits/${visitId}/detail`).then((r) => setData(r.data)).catch(() => onClose());
    // eslint-disable-next-line
  }, [visitId]);
  if (!data) return null;
  const v = data.visit;
  return (
    <div className="fixed inset-0 z-50 bg-navy-900/40 flex justify-end" onClick={onClose} data-testid="visit-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-navy-100 p-5 flex items-center justify-between">
          <div>
            <div className="eb-overline">Visit detail</div>
            <div className="text-lg font-semibold">{v.visitor_name}</div>
          </div>
          <button onClick={onClose} className="btn-ghost"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <Section title="Person">
            {v.photo_url && <img src={v.photo_url} className="w-32 h-32 rounded-lg object-cover mb-3" alt="" />}
            <Row label="Name">{v.visitor_name}</Row>
            <Row label="Company">{v.company || "—"}</Row>
            <Row label="Email">{v.visitor_email || "—"}</Row>
            <Row label="Phone">{v.visitor_phone || "—"}</Row>
          </Section>
          <Section title="Visit">
            <Row label="Site">{v.site_name || v.site_id?.slice(0, 8)}</Row>
            <Row label="Host">{v.host_name || "—"}</Row>
            <Row label="Type">{v.visitor_type}</Row>
            <Row label="Purpose">{v.purpose || "—"}</Row>
            <Row label="Vehicle">{v.vehicle_reg || "—"}</Row>
            <Row label="Method">{v.sign_in_method}</Row>
            <Row label="Signed in">{v.signed_in_at ? formatDateTime(v.signed_in_at) : "—"}</Row>
          </Section>
          <Section title={`History (${data.history_count})`}>
            {data.history.length === 0 ? (
              <p className="text-xs text-navy-400">No prior visits at this org.</p>
            ) : (
              <ul className="text-xs divide-y divide-navy-100">
                {data.history.map((h) => (
                  <li key={h.visit_id} className="py-2 flex items-center justify-between">
                    <div>
                      <div>{h.signed_in_at ? formatDateTime(h.signed_in_at) : "—"}</div>
                      <div className="text-navy-400">{h.visitor_type}{h.purpose ? ` · ${h.purpose}` : ""}</div>
                    </div>
                    <ChevronRight className="w-3 h-3 text-navy-300" />
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Consents">
            {(v.consents || []).length === 0 ? (
              <p className="text-xs text-navy-400">No consents on file.</p>
            ) : (
              <ul className="text-xs space-y-1">
                {v.consents.map((c, i) => (
                  <li key={i}>doc <code>{c.doc_id?.slice(0, 8)}…</code> v{c.version} · accepted {c.accepted_at?.slice(0, 16)}</li>
                ))}
              </ul>
            )}
          </Section>
        </div>
        {v.status === "signed_in" && (
          <div className="p-5 border-t border-navy-100">
            <button onClick={() => onForce(v)} className="btn-ghost text-danger w-full justify-center"
              data-testid="drawer-force-btn">
              <LogOut className="w-4 h-4" /> Force sign-out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ForceModal({ visit, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const id = visit.visit_id || visit.id;
      await api.post(`/live-dashboard/visits/${id}/force-sign-out`, { reason });
      toast.success("Visit force-signed-out");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="force-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-2">Force sign-out</h2>
        <p className="text-sm text-navy-500 mb-3">
          Sign out <strong>{visit.visitor_name}</strong> on their behalf. Audit-logged.
        </p>
        <label className="eb-label">Reason (optional, max 500 chars)</label>
        <textarea rows={3} className="eb-input"
          value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="e.g. left site without signing out"
          data-testid="force-reason-input" />
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-accent" data-testid="force-confirm-btn">
            {busy ? "Signing out…" : "Force sign-out"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="eb-overline mb-2">{title}</div>
      {children}
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div className="text-navy-400 uppercase tracking-wider font-semibold">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}


/**
 * Phase C1.6 — Returning Visitor ROI card on the Live Dashboard.
 *
 * Three render modes:
 *   • DISABLED (`feature_enabled === false`) — muted card prompting the
 *     org admin to flip the toggle in Settings.
 *   • EMPTY    (`total_sign_ins_today === 0`) — orange tile with the
 *     "No sign-ins yet today" copy.
 *   • ACTIVE   — the headline % + minutes-saved figure.
 */
function FastLaneCard({ stats, formatDate, "data-testid": testId }) {
  const {
    total_sign_ins_today: total,
    fast_lane_sign_ins_today: fastLane,
    fast_lane_percentage: pct,
    est_seconds_saved_today: secondsSaved,
    est_minutes_saved_today: minutesSaved,
    est_seconds_saved_per_visit: perVisit,
    feature_enabled: enabled,
    date,
    history,
  } = stats;

  const savedLabel = secondsSaved < 60
    ? `≈ ${secondsSaved}s saved`
    : `≈ ${minutesSaved} minute${minutesSaved === 1 ? "" : "s"} saved`;
  const tooltip = `Time saved estimated at ${perVisit}s per fast-lane sign-in. Configurable in Settings.`;
  let localeDate = date;
  try { if (formatDate) localeDate = formatDate(date); } catch { /* fallback to ISO */ }

  // Phase C1.7 — hide entirely when there's nothing to show ANYWHERE
  // (no sign-ins today AND no sign-ins in the 7-day window). Avoids
  // a chronic empty card on brand-new orgs.
  const historyHasAny = Array.isArray(history) && history.some((h) => h.total_sign_ins > 0);
  if (enabled && total === 0 && !historyHasAny) return null;

  if (!enabled) {
    return (
      <div className="eb-card p-4 mb-4 border border-navy-200 bg-navy-50/40 flex items-center gap-4"
           data-testid={testId} data-state="disabled">
        <RotateCcw className="w-7 h-7 text-navy-300 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-navy-700">Fast lane is disabled</div>
          <div className="text-xs text-navy-500 mt-0.5">
            Returning Visitor fast lane is disabled.{" "}
            <Link to="/settings" className="text-orange-600 hover:text-orange-700 underline"
                  data-testid="fast-lane-settings-link">
              Enable it in Settings
            </Link>{" "}
            to start saving reception time.
          </div>
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="eb-card p-4 mb-4 flex items-center gap-4"
           data-testid={testId} data-state="empty" title={tooltip}>
        <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center shrink-0">
          <RotateCcw className="w-6 h-6 text-orange-500" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-navy-800">Fast lane today</div>
          <div className="text-xs text-navy-500 mt-0.5" data-testid="fast-lane-empty-state">
            No sign-ins yet today.
          </div>
        </div>
        <div className="text-[11px] text-navy-400 hidden md:flex items-center gap-1">
          <Info className="w-3 h-3" /> Returning Visitor · {localeDate}
        </div>
      </div>
    );
  }

  return (
    <div className="eb-card p-4 mb-4 flex items-center gap-4 border border-orange-100/70 bg-gradient-to-r from-orange-50/40 via-white to-white"
         data-testid={testId} data-state="active" title={tooltip}>
      <div className="w-14 h-14 rounded-xl bg-orange-500/10 border border-orange-300/50 flex items-center justify-center shrink-0">
        <RotateCcw className="w-7 h-7 text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-3xl font-bold text-navy-900 tabular-nums"
                data-testid="fast-lane-percentage">
            {Math.round(pct)}%
          </span>
          {/* Sparkline + delta chip render only when history is present. */}
          {Array.isArray(history) && history.length >= 2 && (
            <FastLaneSparkline history={history} todayPct={pct} formatDate={formatDate} />
          )}
          <span className="text-xs text-navy-500">
            of today's sign-ins ({fastLane}/{total})
          </span>
        </div>
        <div className="text-sm text-navy-700 font-medium mt-0.5" data-testid="fast-lane-saved">
          {savedLabel}
        </div>
        <div className="text-[11px] text-navy-400 mt-1 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Returning Visitor fast lane · {localeDate}
        </div>
      </div>
    </div>
  );
}

/**
 * Phase C1.7 — Pure-SVG sparkline + trend chip. No chart library.
 *
 * Renders the trailing 7 daily percentages as a polyline with one dot per
 * day; the most-recent dot is highlighted in the orange accent.  A
 * compact arrow + delta chip shows today's % vs. the 7-day average.
 *
 * Accessibility: every dot has a `<title>` (browsers expose this as a
 * native tooltip), and the SVG carries an aria-label summarising the
 * headline figures so screen-readers don't have to walk the polyline.
 */
function FastLaneSparkline({ history, todayPct, formatDate }) {
  const W = 92, H = 28, P = 3;     // canvas dims; P = inner padding
  const points = history.map((h) => h.fast_lane_percentage || 0);
  const max = Math.max(100, ...points);   // anchor to 100 so swings are honest
  const min = 0;
  const range = max - min || 1;
  const stepX = (W - 2 * P) / (points.length - 1);
  const xy = points.map((p, i) => [
    P + i * stepX,
    H - P - ((p - min) / range) * (H - 2 * P),
  ]);
  const polyline = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

  // Trend chip — today vs. the 7-day mean (rounded to whole %).
  const avg = points.reduce((s, n) => s + n, 0) / points.length;
  const delta = Math.round(todayPct - avg);
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const TrendIcon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const chipCls = dir === "up"
    ? "text-success-700 bg-success-50 border-success-200"
    : dir === "down"
      ? "text-danger-700 bg-danger-50 border-danger-200"
      : "text-navy-500 bg-navy-50 border-navy-200";

  const aria = `7-day fast-lane adoption trend. Today: ${Math.round(todayPct)}%. 7-day average: ${Math.round(avg)}%.`;

  return (
    <span className="inline-flex items-center gap-1.5"
          data-testid="fast-lane-sparkline-wrap">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}
           role="img" aria-label={aria} data-testid="fast-lane-sparkline">
        <polyline points={polyline} fill="none" stroke="#1B2A4A"
                  strokeOpacity="0.5" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" />
        {xy.map(([x, y], i) => {
          const isLast = i === xy.length - 1;
          const h = history[i];
          const dLabel = (() => {
            try { return formatDate ? formatDate(h.date) : h.date; }
            catch { return h.date; }
          })();
          return (
            <circle key={h.date} cx={x} cy={y} r={isLast ? 2.6 : 1.5}
                    fill={isLast ? "#FF6B35" : "#1B2A4A"}
                    fillOpacity={isLast ? 1 : 0.6}>
              <title>{`${dLabel}: ${Math.round(h.fast_lane_percentage)}% (${h.fast_lane_sign_ins}/${h.total_sign_ins})`}</title>
            </circle>
          );
        })}
      </svg>
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${chipCls}`}
            data-testid="fast-lane-trend-chip"
            title={`vs. 7-day average (${Math.round(avg)}%)`}>
        <TrendIcon className="w-3 h-3" />
        {dir === "flat" ? "0%" : `${Math.abs(delta)}%`}
      </span>
    </span>
  );
}
